import { DrawerSelectLocation } from '@/components/sections/builder/elements/body/drawer-select-location';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import useModal from '@/hooks/useModal';
import { calculateDistance, cn, formatPreparationTime, isEmpty } from '@/lib/utils';
import { ThemeColorSchemeType, ThemeType } from '@/stores/builder';
import { HttpTypes } from '@/types';
import { ChevronRight, MapPinIcon } from 'lucide-react';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

type PickupAddressProps = {
  locations?: HttpTypes.StoreOrderLocationPickup[];
  isLoading?: boolean;
  selectedLocation?: HttpTypes.StoreOrderLocationPickup | null;
  setSelectedLocation?: Dispatch<
    SetStateAction<HttpTypes.StoreOrderLocationPickup | HttpTypes.MultiOriginLocation | undefined>
  >;
  setDatePickup?: Dispatch<SetStateAction<Date | undefined>>;
  colorScheme?: ThemeColorSchemeType;
  preparationTime?: number;
  preview?: boolean;
  theme?: ThemeType;
};

export default function PickupAddress({
  locations,
  isLoading,
  selectedLocation,
  setSelectedLocation,
  setDatePickup,
  colorScheme,
  preparationTime,
  preview = false,
  theme,
}: PickupAddressProps) {
  const modalPickup = useModal();

  const [distances, setDistances] = useState<Record<string, number>>({});
  const [sortedLocations, setSortedLocations] = useState<HttpTypes.StoreOrderLocationPickup[]>([]);
  const [geoError, setGeoError] = useState<string | null>(null);

  const didSetInitialLocation = useRef<boolean>(false);

  useEffect(() => {
    if (!locations?.length || didSetInitialLocation.current || preview) return;

    const fallbackSelectLocation = () => {
      const anyWithStock = locations.find((loc) => loc.available_stock > 0);
      setSelectedLocation?.(anyWithStock || undefined);
      setSortedLocations(locations);
      didSetInitialLocation.current = true;
    };

    const handleSuccess = (pos: GeolocationPosition) => {
      const userCoords = {
        lat: pos.coords.latitude,
        lon: pos.coords.longitude,
      };

      const distanceMap: Record<string, number> = {};
      const locationsWithDistance = locations.map((loc) => {
        const distance =
          loc.pinpoint?.lat && loc.pinpoint?.lng
            ? calculateDistance(userCoords.lat, userCoords.lon, loc.pinpoint.lat, loc.pinpoint.lng)
            : Infinity;

        distanceMap[loc.location_id] = distance;
        return { ...loc, _distance: distance };
      });

      setDistances(distanceMap);

      const sorted = locationsWithDistance
        .filter((loc) => loc._distance !== Infinity)
        .sort((a, b) => a._distance - b._distance);

      setSortedLocations(sorted);

      const nearestWithStock = sorted.find((loc) => loc.available_stock > 0);
      if (nearestWithStock) {
        setSelectedLocation?.(nearestWithStock);
      } else {
        fallbackSelectLocation();
      }

      didSetInitialLocation.current = true;
    };

    const handleError = (err: GeolocationPositionError) => {
      if (err.code === err.PERMISSION_DENIED) {
        setGeoError('You denied the location request.');
      } else if (err.code === err.POSITION_UNAVAILABLE) {
        setGeoError('Your location is currently unavailable.');
      } else if (err.code === err.TIMEOUT) {
        setGeoError('Timed out while trying to get your location.');
      } else {
        setGeoError('Unknown location error occurred.');
      }

      fallbackSelectLocation();
    };

    if (navigator.geolocation && !selectedLocation) {
      navigator.geolocation.getCurrentPosition(handleSuccess, handleError, {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      });
    } else {
      fallbackSelectLocation();
    }
  }, [locations, preview, selectedLocation, setSelectedLocation]);

  return (
    <>
      <div className='flex items-center justify-between pb-1'>
        <div className='flex items-center justify-between pb-3 pt-6'>
          <div className='flex items-center gap-x-2'>
            <MapPinIcon className='mt-0.5 h-5 w-5' />
            <h2 className='tmd:text-xl text-lg'>Pickup Address</h2>
          </div>
        </div>
        {(locations?.length ?? 0) > 0 && (
          <Button
            type='button'
            variant='link'
            className={cn(
              'items-center p-0.5 text-sm font-normal underline underline-offset-1',
              !isEmpty(colorScheme) ? 'text-[--btn-background]' : 'button_color_scheme'
            )}
            onClick={() => modalPickup.open()}
          >
            Change
            <ChevronRight className='mt-1 h-5 w-5' />
          </Button>
        )}
      </div>
      <div className='flex items-start justify-start gap-x-2 pb-2'>
        <div className='flex w-full flex-col items-start justify-start'>
          {isLoading ? (
            <div className='w-full'>
              <div className='space-y-2'>
                <Skeleton className='h-4 w-full' />
                <Skeleton className='h-4 w-full' />
              </div>
            </div>
          ) : (locations?.length ?? 0) > 0 ? (
            <>
              <div className='flex w-full items-center justify-between gap-x-2'>
                {selectedLocation ? (
                  <p className='text-sm font-bold'>{selectedLocation?.location_name}</p>
                ) : (
                  <div className='w-full rounded-md bg-gray-100 p-4'>
                    <span className='text-sm font-bold text-gray-500'>
                      Pickup location with available stock not found
                    </span>
                  </div>
                )}
              </div>
              {selectedLocation && (
                <>
                  <span className='line-clamp-1 text-sm'>{selectedLocation?.location_address}</span>
                  <span className='text-xs text-gray-500'>
                    {selectedLocation?.location_id &&
                    distances[selectedLocation?.location_id || ''] !== undefined &&
                    distances[selectedLocation.location_id] !== Infinity
                      ? `± ${(distances[selectedLocation!.location_id] / 1000).toFixed(2)} km - `
                      : 'Unknown distance -'}{' '}
                    Available pickup in {formatPreparationTime(Number(preparationTime))}
                  </span>
                </>
              )}
            </>
          ) : (
            <div className='w-full rounded-md bg-gray-100 p-4'>
              <span className='text-sm font-bold text-gray-500'>No pickup locations available</span>
            </div>
          )}
        </div>
      </div>

      <DrawerSelectLocation
        openModal={modalPickup.isShow}
        setOpenModal={modalPickup.setIsShow}
        locations={sortedLocations.length > 0 ? sortedLocations : locations || []}
        distances={!isEmpty(distances) ? distances : undefined}
        theme={theme}
        preview={preview}
        selectedLocation={selectedLocation as HttpTypes.StoreOrderLocationPickup}
        setSelectedLocation={setSelectedLocation}
        setDatePickup={setDatePickup}
        preparationTime={preparationTime}
        geoErrorCheckout={geoError}
        isCheckoutPage
      />
    </>
  );
}
